import { createSlice, freeze, PayloadAction } from '@reduxjs/toolkit';
// Import Types
import type { ActivePartner, DropdownOption } from 'types';
// Import Store
import { resetPartnerSettings } from './partnerSettings';

type RequestStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface AdminFullPartnersListState {
    status: RequestStatus;
    data: DropdownOption<number, ActivePartner>[];
}

export const initialState: AdminFullPartnersListState = {
    status: 'idle',
    data: [],
};

/**
 * The full partners list is large (tens of thousands of partners), so it is not loaded at app start.
 * It is requested the first time a screen needs it (see `useAdminFullPartnersList`) and kept until the partner changes.
 */
export const adminFullPartnersListSlice = createSlice({
    name: 'adminFullPartnersList',
    initialState,
    reducers: (create) => ({
        getAdminFullPartnersListFetch: create.reducer((state) => {
            state.status = 'loading';
        }),
        getAdminFullPartnersListSucceeded: create.reducer((state, { payload }: PayloadAction<ActivePartner[]>) => {
            state.status = 'succeeded';
            // A frozen value is not walked by Immer, which otherwise traverses and deep-freezes
            // every one of the tens of thousands of partner objects and blocks the main thread.
            state.data = freeze(
                payload?.map((partner) => ({
                    ...partner,
                    value: partner.PartnerId,
                    label: partner.Name,
                })) ?? [],
            );
        }),
        getAdminFullPartnersListFailed: create.reducer((state) => {
            state.status = 'failed';
        }),
        // the screen that requested the list was closed before the response, so the next screen requests it again
        getAdminFullPartnersListCanceled: create.reducer((state) => {
            if (state.status === 'loading') {
                state.status = 'idle';
            }
        }),
    }),
    // the list is requested again after a partner change, as it was when the Header requested it on every mount
    extraReducers: (builder) => {
        builder.addCase(resetPartnerSettings, () => initialState);
    },
    selectors: {
        selectAdminFullPartnersList: (adminFullPartnersList) => adminFullPartnersList.data,
        selectAdminFullPartnersListStatus: (adminFullPartnersList) => adminFullPartnersList.status,
    },
});

export const {
    getAdminFullPartnersListFetch,
    getAdminFullPartnersListSucceeded,
    getAdminFullPartnersListFailed,
    getAdminFullPartnersListCanceled,
} = adminFullPartnersListSlice.actions;
export const { selectAdminFullPartnersList, selectAdminFullPartnersListStatus } = adminFullPartnersListSlice.selectors;
